import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import NewLeadPage, { type CreatedIntakePatient } from '@/pages/leads/NewLeadPage';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface PatientCreateModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Fired after the patient AND its plan are committed. `POST /leads/intake`
   * always creates both, so `planId` is never absent.
   */
  onCreated: (created: CreatedIntakePatient) => void;
  /** Pre-selects the required Plan Setup → Facility field. */
  defaultFacilityId?: number | null;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * The `/leads/new` intake form in a modal, so a patient can be created without
 * leaving whatever is already open (currently the appointment modal).
 *
 * Unmount-on-close is load-bearing: it stands in for the `key={location.key}`
 * remount the route gets in `App.tsx`, and is what guarantees a clean form on
 * every open across the intake form's ~40 state fields, its `applied*Ref`
 * prefill guards and its captured-ID file. It also keeps the form's dozen
 * queries from firing while closed. Do NOT switch to a mounted-but-hidden
 * `open={false}`: `Modal`'s effect else-branch clears
 * `document.body.style.overflow`, unlocking scroll for whatever modal is
 * underneath.
 */
export function PatientCreateModal(props: PatientCreateModalProps) {
  if (!props.open) return null;
  return <PatientCreateModalInner {...props} />;
}

function PatientCreateModalInner({
  onClose,
  onCreated,
  defaultFacilityId,
}: PatientCreateModalProps) {
  // Lifted out of the form so the veil covers the whole panel rather than the
  // scrollable content box — see NewLeadPage's `onSavingChange`.
  const [saving, setSaving] = useState(false);

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Add New Patient"
      size="xxl"
      // The form supplies all of its own padding; Modal's would stack on top,
      // adding parasitic scroll and lifting the sticky footer off the edge.
      bodyClassName="p-0"
      // A stray backdrop click or Escape must not discard a filled-in intake
      // form. Cancel and the X are the only ways out.
      closeOnBackdrop={false}
      closeOnEscape={false}
      overlay={
        saving ? (
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <span className="text-sm font-medium text-slate-600">Creating patient…</span>
          </div>
        ) : undefined
      }
    >
      <NewLeadPage
        embedded
        defaultFacilityId={defaultFacilityId}
        onSuccess={onCreated}
        onCancel={onClose}
        onSavingChange={setSaving}
      />
    </Modal>
  );
}
